import { defineComponent, toRefs, PropType, ref, computed, nextTick, watch } from '@vue/composition-api';
import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  AcoFormData,
  DestinationUrlType,
  ParamsTypesEnum,
  ACTION_TYPE,
  LandingPageStrategy,
  CreativeConfig,
  NormalCreative,
  VerticalCreativeStrategy,
} from '@tt4b/ttam-enums';
import CreativeWebsiteUrl from '../index';
import {
  useCreativeIs,
  useStdCreativeAdgroupIs,
  useCreativeActionTypeIs,
  useCreativeWebsiteUrlValidate,
  useWebsiteFormStatus,
  useIsNotSupportProductLink,
  getOriginalQuery,
  param2Utm,
  generateCustomParams,
  useUtmUpdateHook,
} from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import QuestionTooltip from '@/components/common/question-tooltip/index.vue';
import TtamBackgroundContainer from '@/components/common/background-container/index.vue';
import { useInitRecommUrl } from '../hooks';
import { usePixelDomainProps } from './../../tracking/hooks/usePixel';
import { parseUrl } from 'query-string';
import CatalogCarouselProductsUrlsTipPng from '@tt4b/ttam-assets/src/images/creative/catalog-carousel-products-urls-tip.png';
import CatalogCarouselSwipeLeftTipPng from '@tt4b/ttam-assets/src/images/creative/catalog-carousel-swipe-left-tip.png';
import CreativeProductTileSwipeLeftTipPng from '@tt4b/ttam-assets/src/images/creative/creative-product-tile-swipe-left-tip.png';
import CreativeProductTileTipPng from '@tt4b/ttam-assets/src/images/creative/creative-product-tile-tip.png';
import QuestionTooltipWithImage from '@/components/common/question-tooltip-with-image/index.vue';
import TtamCreativeUtmDialog from '@/components/creative/creative-website-url/utm-dialog/index.vue';
const name = 'ttam-creative-productsales-website-url-compose';
export default defineComponent({
  name,
  components: {
    TtamCreativeUtmDialog,
    QuestionTooltipWithImage,
    TtamBackgroundContainer,
    TtamCreativeWebsiteUrlProductSalesTips: CreativeWebsiteUrl.TtamCreativeWebsiteUrlProductSalesTips,
    TtamCreativeWebsiteUrlProductSalesWebsiteType: CreativeWebsiteUrl.TtamCreativeWebsiteUrlProductSalesWebsiteType,
    TtamCreativeWebsiteUrlInput: CreativeWebsiteUrl.TtamCreativeWebsiteUrlInput,
    TtamCreativeWebsiteUrlRejectReason: CreativeWebsiteUrl.TtamCreativeWebsiteUrlRejectReason,
    TtamCreativeWebsiteUrlCheckUrl: CreativeWebsiteUrl.TtamCreativeWebsiteUrlCheckUrl,
    TtamCreativeUtmEntry: CreativeWebsiteUrl.TtamCreativeUtmEntry,
    QuestionTooltip,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    normalCreativeData: {
      type: Object as PropType<NormalCreative>,
      default: () => ({}),
    },
    activeSplitTestIndex: {
      type: Number,
      default: () => 0,
    },
    creativeConfig: {
      type: Object as PropType<CreativeConfig>,
      default: () => ({}),
    },
    createMode: {
      type: String,
      default: () => ACTION_TYPE.CREATE,
    },
    rejectReason: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    shouldAttachmentCreativeWithLink: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const {
      adgroupData,
      campaignData,
      creativeData,
      creativeConfig,
      createMode,
      normalCreativeData,
      activeSplitTestIndex,
    } = toRefs(props);

    const { isSingleVideo } = useCreativeIs({
      createMode,
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
      normalCreativeData,
    });

    const { isChecking, computedURLSuggtionList, checkExternalUrl } = CreativeWebsiteUrl.useCheckUrl({
      createMode,
      campaignData,
      adgroupData,
      normalCreativeData,
      activeSplitTestIndex,
      creativeData,
    });

    const refExternalUrl = ref<{ clearValidate: (keys: string[]) => void }>();

    const { data: recommendUrls } = useInitRecommUrl({ adgroupData });

    // 获取落地页的domain
    const { initExternalUrlDomain, isPcmPixel } = usePixelDomainProps({
      adgroupData,
      creativeData,
      createMode,
    });

    const changeExternalUrl = async (url = ''): Promise<void> => {
      checkExternalUrl(url);
      const { externalUrlDomain, domain } = await initExternalUrlDomain(url, true);
      emit('external-url-domain-change', externalUrlDomain);
      emit('domain-change', domain);
    };

    const urlChange = ref(false);

    const shareUtmProps = {
      isOpenUrl: false,
      overrideUrl: false,
      openUrlProp: ref(''),
    };

    const originalQuery = getOriginalQuery({
      creativeData,
      ...shareUtmProps,
    });

    const { handleUtmUpdate, checkEmitUtm, hasAppliedUtm, handleAppliedUtm, handleBuilderUtm } = useUtmUpdateHook({
      formData: creativeData,
      emit,
      isVsa: true,
      originalQuery,
      setFormData: () => () => ({}),
      ...shareUtmProps,
    });

    const _externalUrl = computed({
      get() {
        return creativeData.value?.externalUrl ?? '';
      },
      set(newV: string) {
        handleUtmUpdate(newV);
        checkEmitUtm(newV);

        if (newV !== creativeData.value?.externalUrl) {
          urlChange.value = true;
        }

        emit('external-url-change', newV.trim());

        nextTick(() => {
          refExternalUrl.value?.clearValidate?.(['externalUrl']);

          // checkUrl
          changeExternalUrl(newV);
        });
      },
    });

    // 编辑场景或者复制场景下有值，进入页面默认执行一次校验
    if (_externalUrl.value) {
      changeExternalUrl(_externalUrl.value);
    }
    const _paramsType = computed({
      get() {
        return creativeData.value?.paramsType || '';
      },
      set(val) {
        emit('params-type-change', val);
      },
    });

    watch(isPcmPixel, async val => {
      if (val) {
        const { externalUrlDomain, domain } = await initExternalUrlDomain(_externalUrl.value);
        emit('external-url-domain-change', externalUrlDomain);
        emit('domain-change', domain);
      }
    });

    const { isEdit } = useCreativeActionTypeIs({
      createMode,
      adgroupFormData: adgroupData,
    });

    const { isSalesShowExternalUrlType: typeVisible } = useWebsiteFormStatus({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
      creativeFormData: creativeData,
      normalCreativeData,
      createMode,
    });

    const handleApplyUtm = (newParams: string): void => {
      // 除了product link，其他场景都有Url
      const hasUrlScene = !(_paramsType.value === ParamsTypesEnum.ProductDeepLink && typeVisible.value);
      if (!hasAppliedUtm.value) {
        handleAppliedUtm(true);
      }

      if (hasUrlScene) {
        // 通过更新externalUrl，也顺带更新utms参数
        const { url } = parseUrl(_externalUrl.value);
        handleBuilderUtm(true);
        const originCustomParams = generateCustomParams(originalQuery.value);
        _externalUrl.value = originCustomParams
          ? `${url}?${originCustomParams}&${newParams.slice(1)}`
          : `${url}${newParams}`;
      } else {
        // product link的情况，没有externalUrl，就单独更新utms参数
        const newUtms = param2Utm(newParams);
        emit('utms-change', newUtms);
      }
    };

    const isDLP = computed(() => creativeData.value.landingPageStrategy === LandingPageStrategy.DLP);
    const destinationUrlType = computed(() => creativeData.value.destinationUrlType);
    const paramsType = computed(() => creativeData.value.paramsType);
    const isIntantPage = computed(() => creativeData.value.destinationUrlType === DestinationUrlType.instantPage);
    const { isAco, isStandardAdsWithCatalog, isWebsite, isBrandAuction } = useStdCreativeAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    const { salesExternalUrlValidator, salesWebsiteTypeValidator } = useCreativeWebsiteUrlValidate({
      campaignFormData: campaignData,
    });

    const tipsVisible = computed(() => isEdit.value && urlChange.value);

    const inputDisabled = computed(() => false);

    const { isNotSupportProductLink: onlySupportCustomLink } = useIsNotSupportProductLink({
      creativeData,
      adgroupData,
    });
    // 由于解释文案不匹配，smart creative 暂时不用新UI
    const isEntryNewUI = computed(
      () =>
        _paramsType.value === ParamsTypesEnum.CustomeLink &&
        typeVisible.value &&
        onlySupportCustomLink.value &&
        creativeData.value.verticalCreativeStrategy !== VerticalCreativeStrategy.SMART_CREATIVE,
    );

    const inputLabel = computed(() => {
      if (isStandardAdsWithCatalog.value && isWebsite.value && isDLP.value) {
        return i18n.t('module_creative_custom_url');
      }
      if (isStandardAdsWithCatalog.value && isWebsite.value && !isDLP.value && isIntantPage.value) {
        return i18n.t('module_creative_destination_url');
      }
      if (isEntryNewUI.value) {
        return i18n.t('module_adgroup_custome_weblink');
      }

      return i18n.t('adgroup_url');
    });

    const disabledWebsiteType = computed(() => creativeConfig.value.disableWebsiteType);

    // 是否展示落地页文本框
    const inputVisible = computed(() => !isBrandAuction.value || creativeData.value?.enableUrl);

    watch(
      [isDLP, destinationUrlType, paramsType],
      ([newDLP, newUrlType, newParamsType], [oldDLP, oldUrlType, oldParamsType]) => {
        if (newDLP !== oldDLP || newUrlType !== oldUrlType || newParamsType !== oldParamsType) {
          // 切换各种类型，清空utms
          emit('utms-change', []);
        }
      },
    );

    return {
      name,
      salesExternalUrlValidator,
      salesWebsiteTypeValidator,
      _externalUrl,
      _paramsType,
      ParamsTypesEnum,
      tipsVisible,
      disabledWebsiteType,
      isEdit,
      inputDisabled,
      typeVisible,
      inputLabel,
      inputVisible,
      recommendUrls,
      changeExternalUrl,
      originalQuery,
      refExternalUrl,
      isAco,
      isChecking,
      computedURLSuggtionList,
      isDLP,
      handleApplyUtm,
      hasAppliedUtm,
      onlySupportCustomLink,
      isEntryNewUI,
      isSingleVideo,
      // image
      CatalogCarouselProductsUrlsTipPng,
      CatalogCarouselSwipeLeftTipPng,
      CreativeProductTileSwipeLeftTipPng,
      CreativeProductTileTipPng,
    };
  },
});
